import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import usePublicPlans from '../../hooks/usePublicPlans';
import { employerPlans, formatPrice } from '../../utils/plans';

const SIGNUP = '/register?type=employer';

const BENEFITS = [
  ['Wnioski w jednym miejscu', 'Koniec z karteczkami na biurku i prośbami o urlop rozsianymi po SMS-ach i komunikatorach. Każdy wniosek czeka w jednej skrzynce.'],
  ['Pula liczy się sama', 'Widzisz, ile dni zostało każdemu pracownikowi. SIGMA nie przepuści wniosku ponad dostępną pulę.'],
  ['Kalendarz całej firmy', 'Od razu widać, kto i kiedy jest na urlopie. Łatwiej zaplanować pracę i zastępstwa.'],
];

const STEPS = [
  ['Załóż konto firmy', 'Rejestracja trwa kilka minut. Nie podajesz karty.'],
  ['Dodaj pracowników', 'Zaproś ich do SIGMY albo dodaj ręcznie i wpisz pulę urlopową.'],
  ['Zatwierdzaj jednym kliknięciem', 'Pracownik składa wniosek w telefonie, Ty akceptujesz albo odrzucasz z komentarzem.'],
];

const EXTRAS = [
  'Odwołanie zaakceptowanego urlopu, gdy plany się zmienią',
  'Historia wszystkich wniosków każdego pracownika',
  'Ogłoszenia o pracę na tablicy SIGMY',
  'Raporty i dane firmy w ustawieniach',
];

const FAQ = [
  ['Czy darmowy pakiet jest naprawdę darmowy?', 'Tak. Jeden pracownik jest za darmo bez limitu czasu i bez podawania karty. Płacisz dopiero, gdy chcesz dodać kolejnych.'],
  ['Co, gdy dojdę do limitu pracowników?', 'Kolejnego pracownika dodasz po wybraniu większego pakietu. Dane i historia wniosków zostają bez zmian.'],
  ['Czy pracownik musi mieć konto w SIGMIE?', 'Nie musi: możesz dodać go ręcznie i sam prowadzić jego urlopy. Z kontem pracownik składa wnioski z telefonu, a dla niego jest ono bezpłatne.'],
  ['Jak wygląda płatność?', 'Płacisz przez PayU po zalogowaniu na konto firmy. Jeśli potrzebujesz faktury VAT, napisz do nas przez formularz kontaktowy.'],
  ['Czy SIGMA działa na telefonie?', 'Tak. Zarówno Ty, jak i pracownicy możecie korzystać z SIGMY w przeglądarce na telefonie i komputerze.'],
];

function Home() {
  const { plans, loading } = usePublicPlans();
  const paid = employerPlans(plans);

  return (
    <>
      <Helmet>
        <title>SIGMA – wnioski urlopowe pracowników w jednym miejscu</title>
        <meta name="description" content="Pracownik składa wniosek urlopowy w telefonie, Ty zatwierdzasz jednym kliknięciem, a SIGMA sama liczy pulę dni. Jeden pracownik za darmo, bez karty." />
      </Helmet>

      {/* HERO */}
      <section className="pub-section">
        <div className="pub-wrap pub-split">
          <div>
            <div className="pub-kicker">Dla pracodawców i małych firm</div>
            <h1 className="pub-h1">Urlopy w firmie <em>w porządku</em>, bez kartek i&nbsp;SMS-ów.</h1>
            <p className="pub-lead">
              Pracownik składa wniosek w telefonie, Ty zatwierdzasz go jednym kliknięciem, a SIGMA sama pilnuje,
              ile dni urlopu zostało każdemu.
            </p>
            <div className="pub-actions">
              <Link to={SIGNUP} className="pub-btn">Wypróbuj bezpłatnie</Link>
              <Link to="/pricing" className="pub-link">Zobacz cennik</Link>
            </div>
          </div>
          <div className="pub-stats" style={{ borderLeft: '1px solid var(--pub-line)', paddingLeft: 28 }}>
            <div><b>1 klik</b><span>żeby zatwierdzić wniosek urlopowy</span></div>
            <div><b>1 pracownik</b><span>za darmo, bez karty i bez limitu czasu</span></div>
            <div><b>25 lat</b><span>doświadczenia autorki w kształceniu zawodowym</span></div>
          </div>
        </div>
      </section>

      {/* KORZYŚCI */}
      <section className="pub-section--tight" style={{ borderBottom: '1px solid var(--pub-line-soft)' }}>
        <div className="pub-wrap pub-grid-3" style={{ borderTop: 'none' }}>
          {BENEFITS.map(([title, text], i) => (
            <div key={title}>
              <div className="pub-num">{String(i + 1).padStart(2, '0')}</div>
              <h2 className="pub-h3">{title}</h2>
              <p>{text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* JAK TO DZIAŁA */}
      <section className="pub-section pub-section--alt">
        <div className="pub-wrap">
          <h2 className="pub-h2">Jak to działa</h2>
          <p className="pub-muted" style={{ marginBottom: 28 }}>Trzy kroki i urlopy w firmie masz poukładane.</p>
          <div className="pub-grid-3">
            {STEPS.map(([title, text], i) => (
              <div key={title}>
                <div className="pub-num">Krok {i + 1}</div>
                <h3 className="pub-h3">{title}</h3>
                <p>{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CO JESZCZE */}
      <section className="pub-section">
        <div className="pub-wrap pub-split" style={{ alignItems: 'start' }}>
          <div>
            <h2 className="pub-h2">Co jeszcze dostajesz</h2>
            <p className="pub-muted">Wszystko, czego potrzebujesz do spraw urlopowych zespołu, w każdym pakiecie.</p>
          </div>
          <ul className="pub-list">
            {EXTRAS.map(item => <li key={item}>{item}</li>)}
          </ul>
        </div>
      </section>

      {/* CENNIK W SKRÓCIE */}
      <section className="pub-section pub-section--alt">
        <div className="pub-wrap">
          <h2 className="pub-h2">Prosty cennik</h2>
          <p className="pub-muted">Zaczynasz za darmo. Płacisz dopiero, gdy zespół się powiększa.</p>
          <ul className="pub-list" style={{ maxWidth: 560, margin: '24px 0 28px' }}>
            <li><b>Darmowy</b> · 1 pracownik · 0 zł</li>
            {paid.map(p => (
              <li key={p.id}>
                <b>{p.title || p.name}</b>
                {p.worker_limit > 1 && ` · do ${p.worker_limit} pracowników`}
                {' · '}<span>{formatPrice(p.price)}</span> / mies.
              </li>
            ))}
            {loading && <li className="pub-small">Wczytywanie cen…</li>}
          </ul>
          <Link to="/pricing" className="pub-link">Pełny cennik</Link>
        </div>
      </section>

      {/* DLA PRACOWNIKÓW */}
      <section className="pub-section">
        <div className="pub-wrap pub-split">
          <div>
            <h2 className="pub-h2">Twoi pracownicy też na tym zyskują</h2>
            <p className="pub-lead" style={{ marginBottom: 20 }}>
              Pracownik w każdej chwili sprawdza, ile ma urlopu, i widzi status swojego wniosku. Mniej pytań do szefa,
              mniej nieporozumień. Dla pracownika konto jest bezpłatne.
            </p>
            <Link to="/dla-pracownikow" className="pub-link">Zobacz, co dostaje pracownik: Dla pracowników</Link>
          </div>
        </div>
      </section>

      {/* AUTORKA */}
      <section className="pub-section pub-section--alt">
        <div className="pub-wrap pub-author">
          <img src="/about.jpg" alt="Monika Józefowska, autorka SIGMY" style={{ width: '100%', maxWidth: 200, borderRadius: 8 }} />
          <div>
            <div className="pub-kicker">Kto stoi za SIGMĄ</div>
            <h2 className="pub-h2">Monika Józefowska</h2>
            <p className="pub-muted" style={{ maxWidth: '60ch' }}>
              Ekonomistka i doradczyni zawodowa z 25-letnim doświadczeniem w kształceniu zawodowym, manager
              kształcenia zawodowego w Fabryce Mebli WERSAL. SIGMĘ stworzyła, żeby sprawy urlopowe w firmie były proste
              i przejrzyste dla obu stron.
            </p>
            <Link to="/about" className="pub-link">Więcej o nas</Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="pub-section">
        <div className="pub-wrap">
          <h2 className="pub-h2">Najczęstsze pytania</h2>
          <div className="pub-faq">
            {FAQ.map(([q, a]) => (
              <details key={q}>
                <summary>{q}</summary>
                <p>{a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* KOŃCOWE WEZWANIE */}
      <section className="pub-section" style={{ background: 'var(--pub-navy)', color: '#fff', borderBottom: 'none' }}>
        <div className="pub-wrap">
          <h2 className="pub-h2" style={{ color: '#fff' }}>Zacznij za darmo, bez karty.</h2>
          <p style={{ color: '#cbd5e1', marginBottom: 28 }}>Załóż konto firmy i dodaj pierwszego pracownika jeszcze dziś.</p>
          <Link to={SIGNUP} className="pub-btn pub-btn--light">Wypróbuj bezpłatnie</Link>
        </div>
      </section>
    </>
  );
}

export default Home;
